/**
 * 模板系统共享工具函数
 * 前后端通用的模板相关辅助函数
 */

import { TemplateConfig, TemplateId, FieldName } from '../templates/types';

/**
 * 验证模板ID格式
 */
export function isValidTemplateId(templateId: string): boolean {
  // 模板ID应该是kebab-case格式
  const kebabCaseRegex = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
  return kebabCaseRegex.test(templateId);
}

/**
 * 验证模板配置的完整性
 */
export function validateTemplateConfig(config: TemplateConfig): {
  isValid: boolean;
  errors: string[];
} {
  const errors: string[] = [];

  // 验证基础字段
  if (!config.id) {
    errors.push('Missing template ID');
  } else if (!isValidTemplateId(config.id)) {
    errors.push('Invalid template ID format (should be kebab-case)');
  }

  if (!config.name) {
    errors.push('Missing template name');
  }

  if (!config.description) {
    errors.push('Missing template description');
  }

  // 验证字段配置
  if (!config.fieldDefaults) {
    errors.push('Missing fieldDefaults configuration');
  }

  if (!Array.isArray(config.disabledFields)) {
    errors.push('disabledFields must be an array');
  }

  // 验证disabled字段在fieldDefaults中有对应的默认值
  if (config.disabledFields && config.fieldDefaults) {
    config.disabledFields.forEach(fieldName => {
      if (config.fieldDefaults[fieldName] === undefined) {
        errors.push(`Missing default value for disabled field: ${fieldName}`);
      }
    });
  }

  // 验证卡片数量
  if (config.cardCount && config.cardCount <= 0) {
    errors.push('cardCount must be a positive number');
  }

  // 验证生成选项
  if (config.generateOptions) {
    if (typeof config.generateOptions.normal !== 'boolean') {
      errors.push('generateOptions.normal must be boolean');
    }
    if (typeof config.generateOptions.thinking !== 'boolean') {
      errors.push('generateOptions.thinking must be boolean');
    }
  }

  return {
    isValid: errors.length === 0,
    errors
  };
}

/**
 * 深度合并两个对象
 */
export function deepMerge<T extends Record<string, any>>(target: T, source: Partial<T>): T {
  const result = { ...target };
  
  for (const key in source) {
    if (source[key] !== undefined) {
      if (typeof source[key] === 'object' && !Array.isArray(source[key]) && source[key] !== null) {
        result[key] = deepMerge(result[key] || {} as T[Extract<keyof T, string>], source[key]);
      } else {
        result[key] = source[key] as T[Extract<keyof T, string>];
      }
    }
  }
  
  return result;
}

/**
 * 安全地获取嵌套对象属性
 */
export function safeGet<T>(obj: any, path: string, defaultValue: T): T {
  const keys = path.split('.');
  let current = obj;
  
  for (const key of keys) {
    if (current == null || typeof current !== 'object' || !(key in current)) {
      return defaultValue;
    }
    current = current[key];
  }
  
  return current as T;
}

/**
 * 创建模板字段映射表
 */
export function createFieldMap(templates: TemplateConfig[], fieldName: FieldName): Map<TemplateId, any> {
  const map = new Map<TemplateId, any>();
  
  templates.forEach(template => {
    const value = template.fieldDefaults[fieldName];
    if (value !== undefined) {
      map.set(template.id, value);
    }
  });
  
  return map;
}

/**
 * 创建disabled字段映射表
 */
export function createDisabledFieldsMap(templates: TemplateConfig[]): Map<TemplateId, FieldName[]> {
  const map = new Map<TemplateId, FieldName[]>();
  
  templates.forEach(template => {
    map.set(template.id, template.disabledFields);
  });
  
  return map;
}

/**
 * 检查两个模板配置是否一致
 */
export function compareTemplateConfigs(config1: TemplateConfig, config2: TemplateConfig): {
  isEqual: boolean;
  differences: string[];
} {
  const differences: string[] = [];

  // 比较基础字段
  if (config1.id !== config2.id) differences.push('id');
  if (config1.name !== config2.name) differences.push('name');
  if (config1.description !== config2.description) differences.push('description');
  if (config1.cardCount !== config2.cardCount) differences.push('cardCount');

  // 比较disabled字段
  const disabled1 = [...config1.disabledFields].sort();
  const disabled2 = [...config2.disabledFields].sort();
  if (JSON.stringify(disabled1) !== JSON.stringify(disabled2)) {
    differences.push('disabledFields');
  }

  // 比较默认值
  if (JSON.stringify(config1.fieldDefaults) !== JSON.stringify(config2.fieldDefaults)) {
    differences.push('fieldDefaults');
  }

  // 比较生成选项
  if (JSON.stringify(config1.generateOptions) !== JSON.stringify(config2.generateOptions)) {
    differences.push('generateOptions');
  }

  return {
    isEqual: differences.length === 0,
    differences
  };
}

/**
 * 生成模板配置摘要
 */
export function generateTemplateSummary(config: TemplateConfig): string {
  const parts = [
    `ID: ${config.id}`,
    `Name: ${config.name}`,
    `Cards: ${config.cardCount || 'N/A'}`,
    `Disabled: ${config.disabledFields.length} fields`
  ];

  if (config.generateOptions) {
    const modes = [];
    if (config.generateOptions.normal) modes.push('normal');
    if (config.generateOptions.thinking) modes.push('thinking');
    parts.push(`Modes: ${modes.join(', ')}`);
  }

  return parts.join(', ');
}

/**
 * 格式化模板列表为可读字符串
 */
export function formatTemplateList(templates: TemplateConfig[]): string {
  return templates
    .map(template => `- ${template.name} (${template.id})`)
    .join('\n');
}